import {BaseApi} from '@/api/base.api.js'

/**
 * Model API class
 */
export class ModelApi extends BaseApi {

  //Props
  basePath = ''
  itemKey = 'item'
  itemsKey = 'items'
  ModelClass = null
  convertParamKeys = true
  convertDataKeys = true
  convertJsonKeys = true

  /**
   * Query
   */
  query(params, options, path = '/') {
    return this
      .get(path, params, options)
      .then(json => this.toModelCollection(json))
  }

  /**
   * Find by ID
   */
  findById(id) {
    if (!id) {
      throw new Error('Missing ID')
    }
    return this
      .get(`${id}`)
      .then(json => this.toModelInstance(json))
  }

  /**
   * Create
   */
  create(data, params) {
    const {itemKey} = this
    return this
      .post('/', {[itemKey]: data}, {params})
      .then(json => this.toModelInstance(json))
  }

  /**
   * Update by ID
   */
  updateById(id, data, params) {
    if (!id) {
      throw new Error('Missing ID')
    }
    const {itemKey} = this
    return this
      .patch(`${id}`, {[itemKey]: data}, {params})
      .then(json => this.toModelInstance(json))
  }

  /**
   * Remove by ID
   */
  removeById(id, params) {
    if (!id) {
      throw new Error('Missing ID')
    }
    return this
      .delete(`${id}`, params)
  }

  /**
   * Remove many by ID
   */
  removeManyById(ids) {
    return this
      .post('/remove_many', {ids})
  }

  /**
   * Check exists
   */
  checkExists(data) {
    return this
      .post('/check_exists', data)
      .then(data => data.exists)
  }

  /**************************************************************************
   * Data conversion helpers
   ***/

  /**
   * Transform response JSON data to a model collection
   */
  toModelCollection(json) {
    const {itemsKey} = this
    if (!json) {
      throw new Error(`Missing JSON data in response with model collection`)
    }
    if (!json[itemsKey]) {
      throw new Error(`Missing items key ${itemsKey} in response JSON`)
    }
    const items = json[itemsKey].map(item => this.createModel(item))
    return {
      meta: json.meta,
      [itemsKey]: items,
      items,
    }
  }

  /**
   * Transform response JSON data to model instance
   */
  toModelInstance(json) {
    const {itemKey} = this
    if (!json) {
      throw new Error(`Missing JSON data in response with model instance`)
    }
    if (!json[itemKey]) {
      throw new Error(`Missing item key ${itemKey} in response JSON`)
    }
    if (json.meta) {
      return this.toModelInstanceWithMeta(json)
    }
    return this.createModel(json[itemKey])
  }

  /**
   * Transform response JSON data to model instance and preserves meta data
   */
  toModelInstanceWithMeta(json) {
    const {itemKey} = this
    if (!json) {
      throw new Error(`Missing JSON data in response with model instance`)
    }
    if (!json[itemKey]) {
      throw new Error(`Missing item key ${itemKey} in response JSON`)
    }
    const item = this.createModel(json[itemKey])
    return {
      meta: json.meta,
      [itemKey]: item,
      item,
    }
  }

  /**
   * Create model instance from given JSON data
   */
  createModel(json) {
    const {ModelClass} = this
    if (!ModelClass) {
      return json
    }
    return ModelClass.createFrom(json)
  }
}
